export const EXPENSE_CLASSIFICATIONS = [
  "recurring_overhead",
  "one_time_operating",
  "variable_cost",
  "marketing",
  "financing",
  "tax",
  "capital",
  "owner_distribution",
  "non_cash",
  "exclude",
] as const;

export type ExpenseClassification = (typeof EXPENSE_CLASSIFICATIONS)[number];

export const CLASSIFICATION_OPTIONS: Array<{ value: ExpenseClassification; label: string }> = [
  { value: "recurring_overhead", label: "Regular overhead" },
  { value: "one_time_operating", label: "One-time business expense" },
  { value: "marketing", label: "Already counted in marketing" },
  { value: "variable_cost", label: "Already counted in sale costs" },
  { value: "financing", label: "Loan or financing" },
  { value: "tax", label: "Income taxes" },
  { value: "capital", label: "Long-term purchase" },
  { value: "owner_distribution", label: "Owner pay or distribution" },
  { value: "non_cash", label: "Bookkeeping-only expense" },
  { value: "exclude", label: "Do not count this" },
];

export interface ExpenseAllocation {
  classification: ExpenseClassification;
  amount: number;
}
export interface OverheadReviewAmount {
  id: string;
  month: string;
  amount: number;
  allocations: ExpenseAllocation[];
  splitValid: boolean;
}

export interface OverheadReviewAccount {
  id: string;
  name: string;
  path: string;
  providerSection: string;
  suggestedClassification: ExpenseClassification | null;
  suggestionConfidence: "high" | "medium" | "low";
  suggestionReason: string;
  confirmedClassification: ExpenseClassification | null;
  planningMonthlyAmount: number | null;
  suggestedPlanningMonthlyAmount: number;
  planningAmountToUse: number;
  totalAmount: number;
  monthsSeen: number;
  reportMonthCount: number;
  amounts: OverheadReviewAmount[];
}

export interface ManualOverheadItem {
  id: string;
  name: string;
  kind: "recurring" | "one_time";
  amount: number;
  currency: string;
  effectiveMonth: string;
  endMonth: string | null;
  notes: string | null;
}

export interface OverheadReviewView {
  source: {
    integrationId: string;
    platform: string;
    currency: string;
    periodStart: string;
    periodEnd: string;
    reportMonthCount: number;
    fetchedAt: string;
    totalsMatch: boolean;
  } | null;
  accounts: OverheadReviewAccount[];
  manualItems: ManualOverheadItem[];
  summary: {
    normalMonthlyOverhead: number;
    oneTimeExpensesInReviewPeriod: number;
    unreviewedAccountCount: number;
    invalidSplitCount: number;
    readyToUse: boolean;
    approved: boolean;
  };
}

export function classificationLabel(value: ExpenseClassification | null): string {
  if (value === null) return "Choose one";
  return CLASSIFICATION_OPTIONS.find((option) => option.value === value)?.label ?? value;
}
